import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import {
  getCurrentTermsConfig,
  recordTermsAcceptance,
  updateUserTermsAcceptance,
  needsReaccept
} from '../services/termsService';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

/**
 * Parse a full name into first and last name parts
 * @param {string} fullName - The full name to parse
 * @returns {{ firstName: string, lastName: string }}
 */
const parseFullName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return { firstName: '', lastName: '' };
  }

  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  // First part is firstName, rest is lastName
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
};

/**
 * Get user's timezone and locale
 * @returns {{ timezone: string, locale: string }}
 */
const getLocaleInfo = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const locale = navigator.language || 'en-US';
  return { timezone, locale };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [termsConfig, setTermsConfig] = useState(null);
  const [needsTermsReaccept, setNeedsTermsReaccept] = useState(false);
  const [accountDisabled, setAccountDisabled] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setUser(user);

        if (user) {
          // Fetch user profile from Firestore
          const profileRef = doc(db, 'users', user.uid);
          const profileSnap = await getDoc(profileRef);

          if (profileSnap.exists()) {
            // Update existing profile
            const existingProfile = profileSnap.data();

            // Check if user account is disabled
            if (existingProfile.disabled) {
              setAccountDisabled(true);
              setUser(null);
              setUserProfile(null);
              await signOut(auth);
              setLoading(false);
              return;
            }

            // Reset disabled state if user was previously disabled but is now enabled
            setAccountDisabled(false);

            // Sync emailVerified status from Firebase Auth to Firestore
            // This handles when users click the verification link in their email
            if (user.emailVerified && !existingProfile.emailVerified) {
              await setDoc(profileRef, {
                emailVerified: true,
                verificationCode: null,
                codeExpiresAt: null,
                verifiedAt: serverTimestamp(),
                lastLogin: serverTimestamp()
              }, { merge: true });

              // Fetch and set updated profile
              const updatedSnap = await getDoc(profileRef);
              setUserProfile(updatedSnap.data());
            } else {
              // Backfill missing fields for existing users
              const updates = {
                lastLogin: serverTimestamp()
              };

              // Backfill identity fields if missing
              if (!existingProfile.authProvider) {
                // Determine auth provider from providerData
                const providerData = user.providerData?.[0];
                if (providerData?.providerId === 'google.com') {
                  updates.authProvider = 'google';
                } else if (providerData?.providerId === 'apple.com') {
                  updates.authProvider = 'apple';
                } else {
                  updates.authProvider = 'email';
                }
              }

              // Backfill name fields if missing
              if (!existingProfile.fullName || !existingProfile.firstName) {
                // Try to get name from Firebase Auth user object
                const authDisplayName = user.displayName || '';
                if (authDisplayName) {
                  const { firstName, lastName } = parseFullName(authDisplayName);
                  if (!existingProfile.fullName) updates.fullName = authDisplayName;
                  if (!existingProfile.firstName) updates.firstName = firstName;
                  if (!existingProfile.lastName) updates.lastName = lastName;
                  if (!existingProfile.displayName) updates.displayName = firstName || user.email?.split('@')[0] || 'User';
                } else if (!existingProfile.displayName) {
                  // No name available, set displayName from email
                  updates.displayName = user.email?.split('@')[0] || 'User';
                }
              }

              // Backfill photo if missing
              if (!existingProfile.photoURL && user.photoURL) {
                updates.photoURL = user.photoURL;
              }

              // Backfill timezone and locale if missing
              if (!existingProfile.timezone || !existingProfile.locale) {
                const { timezone, locale } = getLocaleInfo();
                if (!existingProfile.timezone) updates.timezone = timezone;
                if (!existingProfile.locale) updates.locale = locale;
              }

              // Initialize onboarding fields if not set (don't auto-complete)
              // Users will see onboarding modal if they haven't filled in optional info
              if (existingProfile.onboardingCompleted === undefined) {
                updates.onboardingCompleted = false;
              }

              // Apply updates
              await setDoc(profileRef, updates, { merge: true });

              // Merge updates with existing profile for state
              setUserProfile({ ...existingProfile, ...updates });
            }
          } else {
            // Create initial profile for new users (OAuth or email/password)
            const newProfile = {
              email: user.email || '',
              emailVerified: user.emailVerified || false,
              isAdmin: false,

              // Subscription fields (legacy + new 3-tier system)
              subscriptionStatus: 'free', // legacy field
              subscriptionTier: 'free', // NEW: 'free' | 'pro' | 'team'
              subscriptionId: null,
              stripeCustomerId: null,
              subscribedAt: null,

              // Team/Organization fields
              organizationId: null, // NEW: Organization ID if member of Team tier
              organizationRole: null, // NEW: 'owner' | 'admin' | 'member'

              // Personal Meeting Room (Pro/Team feature)
              personalRoomId: null, // NEW: Custom meeting room ID
              personalRoomSlug: null, // NEW: URL slug for /p/{slug}

              // Contact & Privacy settings
              profilePublic: false, // NEW: If true, user is searchable in contact search
              contactCode: null, // NEW: 6-char code for private profile users
              contactCodeExpiresAt: null, // NEW: Contact code expiration timestamp

              // Usage tracking
              usage: {
                scheduledMeetingsCount: 0,
                recordingsCount: 0,
                storageUsed: 0, // in bytes
                activePollsCount: 0,
                meetingNotesCount: 0,
              },

              // Timestamps
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp()
            };

            // Write to Firestore with serverTimestamp
            await setDoc(profileRef, newProfile);

            // Fetch the document back to get the actual timestamps
            const updatedProfileSnap = await getDoc(profileRef);
            if (updatedProfileSnap.exists()) {
              setUserProfile(updatedProfileSnap.data());
            } else {
              // Fallback: set profile without serverTimestamp
              setUserProfile({
                email: user.email || '',
                emailVerified: user.emailVerified || false,
                isAdmin: false,
                subscriptionStatus: 'free', // legacy
                subscriptionTier: 'free', // NEW
                subscriptionId: null,
                stripeCustomerId: null,
                subscribedAt: null,
                organizationId: null, // NEW
                organizationRole: null, // NEW
                personalRoomId: null, // NEW
                personalRoomSlug: null, // NEW
                profilePublic: false, // NEW
                contactCode: null, // NEW
                contactCodeExpiresAt: null, // NEW
                usage: {
                  scheduledMeetingsCount: 0,
                  recordingsCount: 0,
                  storageUsed: 0,
                  activePollsCount: 0,
                  meetingNotesCount: 0,
                },
                createdAt: new Date(),
                lastLogin: new Date()
              });
            }
          }
        } else {
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Load terms config and check re-acceptance status
  useEffect(() => {
    const checkTermsStatus = async () => {
      try {
        const config = await getCurrentTermsConfig();
        setTermsConfig(config);

        if (userProfile) {
          const userTermsNeedsReaccept = needsReaccept(
            userProfile.termsAcceptedVersion,
            config.requiredVersion
          );
          const userPrivacyNeedsReaccept = needsReaccept(
            userProfile.privacyAcceptedVersion,
            config.privacyVersion
          );
          setNeedsTermsReaccept(userTermsNeedsReaccept || userPrivacyNeedsReaccept);
        }
      } catch (error) {
        console.error('Error checking terms status:', error);
      }
    };

    checkTermsStatus();
  }, [userProfile]);

  const signup = async (email, password, termsData = {}) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Generate 6-digit verification code
    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();

    // Get terms versions and full name
    const { termsVersion = '1.0.0', privacyVersion = '1.0.0', fullName = '' } = termsData;

    // Parse name into parts
    const { firstName, lastName } = parseFullName(fullName);
    const displayName = firstName || email.split('@')[0]; // Default to email prefix if no name

    // Get timezone and locale
    const { timezone, locale } = getLocaleInfo();

    // Store verification code in Firestore with terms acceptance
    await setDoc(doc(db, 'users', result.user.uid), {
      email: email,
      emailVerified: false,
      isAdmin: false,
      verificationCode: verificationCode,
      codeExpiresAt: new Date(Date.now() + 15 * 60 * 1000), // 15 minutes

      // Identity fields
      fullName: fullName.trim(),
      firstName,
      lastName,
      displayName,
      authProvider: 'email',
      photoURL: null,

      // Locale info (auto-detected)
      timezone,
      locale,

      // Terms & Conditions acceptance
      termsAcceptedVersion: termsVersion,
      termsAcceptedAt: serverTimestamp(),
      privacyAcceptedVersion: privacyVersion,
      privacyAcceptedAt: serverTimestamp(),

      // Onboarding fields (optional, filled post-signup)
      industry: null,
      companySize: null,
      useCases: [],
      onboardingCompleted: false,

      // Subscription fields (legacy + new 3-tier system)
      subscriptionStatus: 'free', // legacy field
      subscriptionTier: 'free', // NEW: 'free' | 'pro' | 'team'
      subscriptionId: null,
      stripeCustomerId: null,
      subscribedAt: null,

      // Team/Organization fields
      organizationId: null, // NEW
      organizationRole: null, // NEW

      // Personal Meeting Room (Pro/Team feature)
      personalRoomId: null, // NEW
      personalRoomSlug: null, // NEW

      // Privacy & Search settings
      profilePublic: false, // Searchable by email/phone (user must opt-in)

      // Usage tracking
      usage: {
        scheduledMeetingsCount: 0,
        recordingsCount: 0,
        storageUsed: 0,
        activePollsCount: 0,
        meetingNotesCount: 0,
      },

      // Timestamps
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp()
    });

    // Record terms acceptance in audit trail
    try {
      await recordTermsAcceptance({
        userId: result.user.uid,
        termsVersion,
        privacyVersion,
        source: 'web',
        context: 'signup'
      });
    } catch (error) {
      console.error('Error recording terms acceptance:', error);
      // Don't fail signup if audit trail fails
    }

    // Send verification email with code (only for email/password users)
    try {
      await sendVerificationEmail(result.user, verificationCode);
    } catch (error) {
      console.error('Error sending verification email:', error);
      // Don't fail signup if email fails to send
    }

    return result;
  };

  const sendVerificationEmail = async (user, code) => {
    // Send verification email with 6-digit code via our API
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: user.email,
        subject: 'Verify your QuickMeet email',
        templateId: 'email_verification',
        templateData: {
          firstName: user.displayName?.split(' ')[0] || 'there',
          verificationCode: code,
          expiresIn: '15 minutes'
        },
        type: 'transactional',
        userId: user.uid,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send verification email');
    }
  };

  const verifyEmailCode = async (code) => {
    if (!user) throw new Error('No user logged in');

    const profileRef = doc(db, 'users', user.uid);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
      throw new Error('User profile not found');
    }

    const profile = profileSnap.data();

    // Check if code matches and hasn't expired
    if (profile.verificationCode !== code) {
      throw new Error('Invalid verification code');
    }

    if (new Date() > profile.codeExpiresAt?.toDate()) {
      throw new Error('Verification code has expired');
    }

    // Mark email as verified - use merge to preserve other fields
    await setDoc(profileRef, {
      emailVerified: true,
      verificationCode: null,
      codeExpiresAt: null,
      verifiedAt: serverTimestamp()
    }, { merge: true });

    // Update local state - merge with existing profile to preserve all fields
    setUserProfile(prev => ({ ...prev, emailVerified: true, verificationCode: null, codeExpiresAt: null }));

    return true;
  };

  const resendVerificationCode = async () => {
    if (!user) throw new Error('No user logged in');

    // Generate new code
    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();

    const profileRef = doc(db, 'users', user.uid);
    await setDoc(profileRef, {
      verificationCode: verificationCode,
      codeExpiresAt: new Date(Date.now() + 15 * 60 * 1000)
    }, { merge: true });

    await sendVerificationEmail(user, verificationCode);
  };

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Update last login time
    const profileRef = doc(db, 'users', result.user.uid);
    await setDoc(profileRef, {
      lastLogin: serverTimestamp()
    }, { merge: true });

    return result;
  };

  const logout = () => {
    return signOut(auth);
  };

  const signInWithGoogle = async (termsData = {}) => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      console.log('Google sign-in successful:', result.user.email);

      // Get Google user data
      const googleUser = result.user;
      const fullName = googleUser.displayName || '';
      const { firstName, lastName } = parseFullName(fullName);
      const displayName = firstName || googleUser.email?.split('@')[0] || 'User';
      const { timezone, locale } = getLocaleInfo();

      // Check if user profile already exists
      const profileRef = doc(db, 'users', result.user.uid);
      const profileSnap = await getDoc(profileRef);

      // If new user (no profile exists), create full profile
      if (!profileSnap.exists()) {
        await setDoc(profileRef, {
          email: googleUser.email || '',
          emailVerified: googleUser.emailVerified || true, // Google accounts are pre-verified
          isAdmin: false,

          // Identity fields from Google
          fullName,
          firstName,
          lastName,
          displayName,
          authProvider: 'google',
          photoURL: googleUser.photoURL || null,

          // Locale info (auto-detected)
          timezone,
          locale,

          // Terms & Conditions (auto-accept for OAuth)
          termsAcceptedVersion: termsData.termsVersion || '1.0.0',
          termsAcceptedAt: serverTimestamp(),
          privacyAcceptedVersion: termsData.privacyVersion || '1.0.0',
          privacyAcceptedAt: serverTimestamp(),

          // Onboarding fields (optional, filled post-signup)
          industry: null,
          companySize: null,
          useCases: [],
          onboardingCompleted: false,

          // Subscription fields
          subscriptionStatus: 'free',
          subscriptionTier: 'free',
          subscriptionId: null,
          stripeCustomerId: null,
          subscribedAt: null,

          // Team/Organization fields
          organizationId: null,
          organizationRole: null,

          // Personal Meeting Room
          personalRoomId: null,
          personalRoomSlug: null,

          // Privacy & Search settings
          profilePublic: false,

          // Usage tracking
          usage: {
            scheduledMeetingsCount: 0,
            recordingsCount: 0,
            storageUsed: 0,
            activePollsCount: 0,
            meetingNotesCount: 0,
          },

          // Timestamps
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp()
        });

        // Record in audit trail
        try {
          await recordTermsAcceptance({
            userId: result.user.uid,
            termsVersion: termsData.termsVersion || '1.0.0',
            privacyVersion: termsData.privacyVersion || '1.0.0',
            source: 'web',
            context: 'signup'
          });
        } catch (error) {
          console.error('Error recording terms acceptance:', error);
        }
      } else {
        // Existing user - just update last login and ensure name fields exist
        const existingData = profileSnap.data();
        const updates = {
          lastLogin: serverTimestamp()
        };

        // If user doesn't have name fields, add them from Google
        if (!existingData.fullName && fullName) {
          updates.fullName = fullName;
          updates.firstName = firstName;
          updates.lastName = lastName;
          updates.displayName = existingData.displayName || displayName;
        }

        // Update photo if not set
        if (!existingData.photoURL && googleUser.photoURL) {
          updates.photoURL = googleUser.photoURL;
        }

        await setDoc(profileRef, updates, { merge: true });
      }

      return result;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  const signInWithApple = async (termsData = {}) => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    try {
      const result = await signInWithPopup(auth, provider);
      console.log('Apple sign-in successful:', result.user.email);

      // Get Apple user data
      // IMPORTANT: Apple only provides name on FIRST signup, not on subsequent logins
      const appleUser = result.user;

      // Try to get name from the OAuth credential (only available on first signup)
      const credential = OAuthProvider.credentialFromResult(result);
      let appleFirstName = '';
      let appleLastName = '';

      // Apple provides name in the additionalUserInfo on first sign-in
      if (result._tokenResponse?.firstName) {
        appleFirstName = result._tokenResponse.firstName;
      }
      if (result._tokenResponse?.lastName) {
        appleLastName = result._tokenResponse.lastName;
      }

      // Also try displayName from the user object
      const fullNameFromDisplay = appleUser.displayName || '';
      const parsedFromDisplay = parseFullName(fullNameFromDisplay);

      // Use Apple-provided names first, then fallback to displayName parsing
      const firstName = appleFirstName || parsedFromDisplay.firstName || '';
      const lastName = appleLastName || parsedFromDisplay.lastName || '';
      const fullName = firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || fullNameFromDisplay);
      const displayName = firstName || appleUser.email?.split('@')[0] || 'User';
      const { timezone, locale } = getLocaleInfo();

      // Check if user profile already exists
      const profileRef = doc(db, 'users', result.user.uid);
      const profileSnap = await getDoc(profileRef);

      // If new user (no profile exists), create full profile
      // CRITICAL: We must capture the name NOW because Apple won't send it again
      if (!profileSnap.exists()) {
        await setDoc(profileRef, {
          email: appleUser.email || '',
          emailVerified: true, // Apple accounts are verified
          isAdmin: false,

          // Identity fields from Apple (CAPTURED ON FIRST SIGNUP ONLY)
          fullName,
          firstName,
          lastName,
          displayName,
          authProvider: 'apple',
          photoURL: appleUser.photoURL || null,

          // Flag to indicate if name was captured
          nameProvided: !!(firstName || lastName),
          nameMissing: !(firstName || lastName), // Flag for prompting user

          // Locale info (auto-detected)
          timezone,
          locale,

          // Terms & Conditions (auto-accept for OAuth)
          termsAcceptedVersion: termsData.termsVersion || '1.0.0',
          termsAcceptedAt: serverTimestamp(),
          privacyAcceptedVersion: termsData.privacyVersion || '1.0.0',
          privacyAcceptedAt: serverTimestamp(),

          // Onboarding fields (optional, filled post-signup)
          industry: null,
          companySize: null,
          useCases: [],
          onboardingCompleted: false,

          // Subscription fields
          subscriptionStatus: 'free',
          subscriptionTier: 'free',
          subscriptionId: null,
          stripeCustomerId: null,
          subscribedAt: null,

          // Team/Organization fields
          organizationId: null,
          organizationRole: null,

          // Personal Meeting Room
          personalRoomId: null,
          personalRoomSlug: null,

          // Privacy & Search settings
          profilePublic: false,

          // Usage tracking
          usage: {
            scheduledMeetingsCount: 0,
            recordingsCount: 0,
            storageUsed: 0,
            activePollsCount: 0,
            meetingNotesCount: 0,
          },

          // Timestamps
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp()
        });

        // Record in audit trail
        try {
          await recordTermsAcceptance({
            userId: result.user.uid,
            termsVersion: termsData.termsVersion || '1.0.0',
            privacyVersion: termsData.privacyVersion || '1.0.0',
            source: 'web',
            context: 'signup'
          });
        } catch (error) {
          console.error('Error recording terms acceptance:', error);
        }
      } else {
        // Existing user - just update last login
        // NOTE: We do NOT update name from Apple here because Apple won't send it
        // The user's stored name from first signup is the source of truth
        await setDoc(profileRef, {
          lastLogin: serverTimestamp()
        }, { merge: true });
      }

      return result;
    } catch (error) {
      console.error('Apple sign-in error:', error);
      throw error;
    }
  };

  const requestCalendarAccess = async () => {
    if (!user) {
      throw new Error('User must be signed in to request calendar access');
    }

    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/calendar.events');

    try {
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;

      if (accessToken) {
        // Store the access token in user profile
        const profileRef = doc(db, 'users', user.uid);
        await setDoc(profileRef, {
          googleCalendarToken: accessToken,
          calendarAccessGranted: true,
          calendarTokenExpiry: new Date(Date.now() + 3600 * 1000) // 1 hour from now
        }, { merge: true });

        // Update local profile
        const updatedProfile = await getDoc(profileRef);
        setUserProfile(updatedProfile.data());

        return accessToken;
      }

      throw new Error('No access token received');
    } catch (error) {
      console.error('Calendar access error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    const actionCodeSettings = {
      url: window.location.origin || 'https://quickmeet.cam',
      handleCodeInApp: false
    };

    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  };

  // Accept updated terms (for re-acceptance flow)
  const acceptTerms = async () => {
    if (!user || !termsConfig) {
      throw new Error('User must be logged in to accept terms');
    }

    const termsVersion = termsConfig.requiredVersion;
    const privacyVersion = termsConfig.privacyVersion;

    // Update user profile
    await updateUserTermsAcceptance(user.uid, termsVersion, privacyVersion);

    // Record in audit trail
    await recordTermsAcceptance({
      userId: user.uid,
      termsVersion,
      privacyVersion,
      source: 'web',
      context: 'login_reaccept'
    });

    // Update local state
    setUserProfile(prev => ({
      ...prev,
      termsAcceptedVersion: termsVersion,
      termsAcceptedAt: new Date(),
      privacyAcceptedVersion: privacyVersion,
      privacyAcceptedAt: new Date()
    }));

    setNeedsTermsReaccept(false);
  };

  const value = {
    user,
    userProfile,
    signup,
    login,
    logout,
    signInWithGoogle,
    signInWithApple,
    requestCalendarAccess,
    verifyEmailCode,
    resendVerificationCode,
    resetPassword,
    // Terms-related exports
    termsConfig,
    needsTermsReaccept,
    acceptTerms,
    // Account status
    accountDisabled
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
